import { DashboardPreferenceBlock } from '@interfaces/dashboard-preferences/responses/dashboard-preferences.response';
import { HomeBlockId, HomeLayoutItem, isHomeBlockId } from './home-blocks';

/**
 * Arrumação da Home que vale para a pessoa agora (card 3788).
 *
 * - Aplica a ordem e o `visible` gravados.
 * - Ignora id desconhecido, repetido ou de bloco que não está disponível para ela (flag desligada, sem permissão).
 * - Acrescenta no fim, ligados e na ordem de `available`, os blocos disponíveis que não estão na lista gravada (bloco
 *   novo no futuro, ou que ficou disponível depois que ela salvou).
 *
 * Lista gravada vazia ou ausente = ordem de `available` com tudo ligado (o padrão).
 * @param saved Arrumação gravada (servidor ou navegador).
 * @param available Blocos disponíveis para a pessoa, na ordem padrão.
 */
export function resolveHomeLayout(
  saved: readonly DashboardPreferenceBlock[] | null | undefined,
  available: readonly HomeBlockId[]
): HomeLayoutItem[] {
  const availableSet = new Set<HomeBlockId>(available);
  const seen = new Set<HomeBlockId>();
  const result: HomeLayoutItem[] = [];

  for (const block of saved ?? []) {
    const id = block?.id;
    if (!isHomeBlockId(id) || !availableSet.has(id) || seen.has(id)) {
      continue;
    }
    seen.add(id);
    result.push({ id, visible: block.visible !== false });
  }

  for (const id of available) {
    if (!seen.has(id)) {
      seen.add(id);
      result.push({ id, visible: true });
    }
  }

  return result;
}

/**
 * Monta o que vai ser gravado a partir do que a pessoa deixou na gaveta, sem perder os blocos que não apareceram nela
 * (indisponíveis agora, ex.: flag desligada hoje): eles voltam para perto de onde estavam, logo depois do bloco que os
 * precedia na lista gravada (ou no começo), com o `visible` que tinham.
 * @param edited Blocos da gaveta, na ordem nova.
 * @param saved Arrumação gravada antes.
 */
export function mergeWithUnavailableBlocks(
  edited: readonly HomeLayoutItem[],
  saved: readonly DashboardPreferenceBlock[] | null | undefined
): DashboardPreferenceBlock[] {
  const result: DashboardPreferenceBlock[] = edited.map(item => ({
    id: item.id,
    visible: item.visible,
  }));
  const present = new Set<string>(result.map(item => item.id));
  const previous = (saved ?? []).filter(block => isHomeBlockId(block?.id));

  previous.forEach((block, index) => {
    if (present.has(block.id)) {
      return;
    }
    present.add(block.id);
    const before = previous
      .slice(0, index)
      .reverse()
      .find(candidate => result.some(item => item.id === candidate.id));
    const position = before ? result.findIndex(item => item.id === before.id) + 1 : 0;
    result.splice(position, 0, { id: block.id, visible: block.visible !== false });
  });

  return result;
}

/**
 * Diz se duas arrumações são iguais (mesma ordem e mesmo `visible`): a gaveta usa para saber se há mudança não salva.
 * @param a Arrumação.
 * @param b Outra arrumação.
 */
export function sameHomeLayout(
  a: readonly HomeLayoutItem[],
  b: readonly HomeLayoutItem[]
): boolean {
  return (
    a.length === b.length &&
    a.every((item, index) => item.id === b[index].id && item.visible === b[index].visible)
  );
}
